import { Component, createSignal, For, Show, createResource, Switch, Match } from 'solid-js'
import { i18nStore, userStore, chatStore } from '../../store'
import { Search, Flame, Trophy, Gem, MessageSquare } from 'lucide-solid'
import { getAssetUrl } from '../../shared/util'
import { api } from '../../store/api'
import { useNavigate } from '@solidjs/router'
import { quickCreateChat } from '../../store/chat'

const TrendingPage: Component = () => {
  const i18n = i18nStore((s) => ({ t: s.t }))
  const user = userStore((s) => ({ profile: s.profile }))
  const nav = useNavigate()
  const [searchType, setSearchType] = createSignal<'character' | 'author'>('character')
  
  // Character Tab State
  const [rankRange, setRankRange] = createSignal('TOP10')
  const [rankTime, setRankTime] = createSignal('daily')
  const [rankType, setRankType] = createSignal('views')

  // Author Tab State
  const [authorRankTime, setAuthorRankTime] = createSignal('total')
  const [authorRankType, setAuthorRankType] = createSignal('tips')

  // Fetch Hot Tags and Keywords
  const [trendingTags] = createResource(async () => {
    const { result } = await api.get('/trending/tags')
    return result || { hotTags: [], hotKeywords: [] }
  })

  // Fetch Character Rankings
  const [characterRankings] = createResource(
    () => ({ range: rankRange(), time: rankTime(), type: rankType() }),
    async (params) => {
      const { result } = await api.get('/trending/characters', params)
      return result?.rankings || []
    }
  )

  // Fetch Author Rankings
  const [authorRankings] = createResource(
    () => ({ range: rankRange(), time: authorRankTime(), type: authorRankType() }),
    async (params) => {
      const { result } = await api.get('/trending/authors', params)
      return result?.rankings || []
    }
  )

  const getRankColor = (id: number) => {
    if (id === 1) return 'bg-pink-500'
    if (id === 2) return 'bg-green-500'
    return 'bg-slate-800'
  }

  const handleCharacterClick = (charId: string) => {
    quickCreateChat(charId, nav)
  }

  const handleAuthorClick = (userId: string) => {
    nav(`/user/${userId}`)
  }

  return (
    <div class="flex flex-col gap-6 p-4 pb-20 bg-[var(--bg-900)] min-h-screen text-[var(--bg-100)]">
      {/* Search Bar */}
      <div class="relative">
        <div class="absolute inset-y-0 left-3 flex items-center pointer-events-none text-[var(--bg-400)]">
          <Search size={18} />
        </div>
        <input
          type="text"
          placeholder={i18n.t.search_placeholder}
          class="w-full rounded-full bg-[var(--bg-800)] border border-[var(--bg-700)] py-2 pl-10 pr-4 text-sm focus:border-[var(--hl-500)] focus:outline-none transition-all"
        />
      </div>

      {/* Search Type Switcher */}
      <div class="flex p-1 rounded-xl bg-[var(--bg-800)] border border-[var(--bg-700)]">
        <button 
          onClick={() => setSearchType('character')}
          class="flex-1 py-2 text-sm font-bold rounded-lg transition-all"
          classList={{ 'bg-[var(--bg-700)] text-white': searchType() === 'character', 'text-[var(--bg-400)]': searchType() !== 'character' }}
        >
          {i18n.t.nav_home}
        </button>
        <button 
          onClick={() => setSearchType('author')}
          class="flex-1 py-2 text-sm font-bold rounded-lg transition-all"
          classList={{ 'bg-[var(--bg-700)] text-white': searchType() === 'author', 'text-[var(--bg-400)]': searchType() !== 'author' }}
        >
          {i18n.t.trending_author}
        </button>
      </div>

      <Switch>
        {/* CHARACTER TAB VIEW */}
        <Match when={searchType() === 'character'}>
          {/* Hot Keywords */}
          <div class="flex flex-wrap gap-2">
            <For each={trendingTags()?.hotTags}>
              {(tag) => (
                <span class="px-4 py-1.5 rounded-full bg-[var(--bg-800)] border border-[var(--bg-700)] text-xs font-medium hover:border-[var(--hl-500)] cursor-pointer transition-all">
                  {tag}
                </span>
              )}
            </For>
          </div>

          {/* Hot Tags Section */}
          <div class="flex flex-col gap-3">
            <div class="flex items-center gap-2 text-sm font-bold">
              <Flame size={16} class="text-orange-500" />
              {i18n.t.trending_hot_tags}
            </div>
            <div class="flex flex-wrap gap-2">
              <For each={trendingTags()?.hotKeywords}>
                {(tag) => (
                  <span class="px-4 py-1.5 rounded-full bg-[var(--bg-800)] border border-[var(--bg-700)] text-xs font-medium hover:bg-[var(--bg-700)] cursor-pointer transition-all">
                    {tag}
                  </span>
                )}
              </For>
            </div>
          </div>

          {/* Ranking Section */}
          <div class="flex flex-col gap-4">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2 text-sm font-bold">
                <Trophy size={16} class="text-yellow-500" />
                {i18n.t.trending_ranking}
              </div>
              <div class="flex p-0.5 rounded-lg bg-[var(--bg-800)] text-[10px]">
                <For each={['TOP10', 'TOP30', 'TOP50']}>
                  {(range) => (
                    <button 
                      onClick={() => setRankRange(range)}
                      class="px-2 py-1 rounded-md transition-all"
                      classList={{ 'bg-[var(--bg-700)] text-white': rankRange() === range, 'text-[var(--bg-400)]': rankRange() !== range }}
                    >
                      {range}
                    </button>
                  )}
                </For>
              </div>
            </div>

            {/* Rank Filters */}
            <div class="flex justify-between items-center">
              <div class="flex gap-1 p-0.5 rounded-lg bg-[var(--bg-800)] text-[10px]">
                <button onClick={() => setRankTime('daily')} class="px-3 py-1 rounded-md" classList={{ 'bg-[var(--bg-700)] text-white': rankTime() === 'daily' }}>{i18n.t.trending_daily}</button>
                <button onClick={() => setRankTime('weekly')} class="px-3 py-1 rounded-md" classList={{ 'bg-[var(--bg-700)] text-white': rankTime() === 'weekly' }}>{i18n.t.trending_weekly}</button>
                <button onClick={() => setRankTime('monthly')} class="px-3 py-1 rounded-md" classList={{ 'bg-[var(--bg-700)] text-white': rankTime() === 'monthly' }}>{i18n.t.trending_monthly}</button>
              </div>
              <div class="flex gap-1 p-0.5 rounded-lg bg-[var(--bg-800)] text-[10px]">
                <button onClick={() => setRankType('views')} class="px-3 py-1 rounded-md" classList={{ 'bg-[var(--bg-700)] text-white': rankType() === 'views' }}>{i18n.t.trending_views}</button>
                <button onClick={() => setRankType('likes')} class="px-3 py-1 rounded-md" classList={{ 'bg-[var(--bg-700)] text-white': rankType() === 'likes' }}>{i18n.t.trending_likes}</button>
                <button onClick={() => setRankType('favorites')} class="px-3 py-1 rounded-md" classList={{ 'bg-[var(--bg-700)] text-white': rankType() === 'favorites' }}>{i18n.t.trending_favorites}</button>
                <button onClick={() => setRankType('chats')} class="px-3 py-1 rounded-md" classList={{ 'bg-[var(--bg-700)] text-white': rankType() === 'chats' }}>{i18n.t.trending_chats}</button>
              </div>
            </div>

            {/* Ranking List */}
            <div class="flex flex-col gap-3">
              <Show when={!characterRankings.loading} fallback={<div class="text-center py-10 opacity-50">Loading...</div>}>
                <For each={characterRankings()}>
                  {(item) => (
                    <div 
                      onClick={() => handleCharacterClick(item._id)}
                      class={`flex items-center gap-3 p-3 rounded-2xl border border-[var(--bg-700)] ${getRankColor(item.id)} bg-opacity-20 transition-all hover:scale-[1.01] cursor-pointer`}
                    >
                      <div class="text-xl font-black italic w-6 text-center">{item.id}</div>
                      <div class="h-12 w-12 rounded-full bg-[var(--bg-700)] overflow-hidden border-2 border-white/20">
                        <Show when={item.avatar} fallback={<div class="w-full h-full bg-gradient-to-br from-gray-400 to-gray-600" />}>
                          <img src={getAssetUrl(item.avatar)} class="w-full h-full object-cover" />
                        </Show>
                      </div>
                      <div class="flex-1 min-w-0">
                        <div class="text-sm font-bold truncate">{item.name}</div>
                        <div class="text-[10px] text-white/60 line-clamp-1">{item.desc}</div>
                      </div>
                      <div class="flex flex-col items-end gap-1">
                        <div class="flex items-center gap-1 text-[var(--hl-500)] font-bold">
                          <Flame size={12} />
                          <span class="text-xs">{item.score}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </For>
              </Show>
            </div>
          </div>
        </Match>

        {/* AUTHOR TAB VIEW */}
        <Match when={searchType() === 'author'}>
          {/* User Rank Card */}
          <div 
            onClick={() => handleAuthorClick(user.profile?.userId!)}
            class="flex items-center gap-4 p-4 rounded-2xl bg-[var(--bg-800)] border border-[var(--bg-700)] cursor-pointer"
          >
            <div class="text-2xl font-black italic text-[var(--bg-400)]">10</div>
            <div class="h-12 w-12 rounded-full bg-[var(--bg-700)] overflow-hidden border-2 border-white/10">
              <Show when={user.profile?.avatar} fallback={<div class="w-full h-full bg-gradient-to-br from-blue-400 to-blue-600" />}>
                <img src={getAssetUrl(user.profile?.avatar!)} class="w-full h-full object-cover" />
              </Show>
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-lg font-bold truncate">{user.profile?.handle || 'Geng'}</div>
              <div class="text-xs text-[var(--bg-400)]">{user.profile?.bio || '随缘更'}</div>
            </div>
            <div class="flex items-center gap-1 text-[var(--bg-400)]">
              <MessageSquare size={16} />
              <span class="text-sm font-medium">{user.profile?.followerCount || 0}</span>
            </div>
          </div>

          {/* Author Ranking Section */}
          <div class="flex flex-col gap-4">
            <div class="flex items-center justify-between">
              <div class="text-sm font-bold">{i18n.t.trending_author_ranking_title}</div>
              <div class="flex p-0.5 rounded-lg bg-[var(--bg-800)] text-[10px]">
                <For each={['TOP10', 'TOP30', 'TOP50']}>
                  {(range) => (
                    <button 
                      onClick={() => setRankRange(range)}
                      class="px-2 py-1 rounded-md transition-all"
                      classList={{ 'bg-[var(--bg-700)] text-white': rankRange() === range, 'text-[var(--bg-400)]': rankRange() !== range }}
                    >
                      {range}
                    </button>
                  )}
                </For>
              </div>
            </div>

            {/* Author Rank Filters */}
            <div class="flex justify-between items-center">
              <div class="flex gap-1 p-0.5 rounded-lg bg-[var(--bg-800)] text-[10px]">
                <button onClick={() => setAuthorRankTime('total')} class="px-3 py-1 rounded-md" classList={{ 'bg-[var(--bg-700)] text-white': authorRankTime() === 'total' }}>{i18n.t.trending_author_total}</button>
                <button onClick={() => setAuthorRankTime('weekly')} class="px-3 py-1 rounded-md" classList={{ 'bg-[var(--bg-700)] text-white': authorRankTime() === 'weekly' }}>{i18n.t.trending_weekly}</button>
                <button onClick={() => setAuthorRankTime('monthly')} class="px-3 py-1 rounded-md" classList={{ 'bg-[var(--bg-700)] text-white': authorRankTime() === 'monthly' }}>{i18n.t.trending_monthly}</button>
              </div>
              <div class="flex gap-1 p-0.5 rounded-lg bg-[var(--bg-800)] text-[10px]">
                <button onClick={() => setAuthorRankType('tips')} class="px-3 py-1 rounded-md" classList={{ 'bg-[var(--bg-700)] text-white': authorRankType() === 'tips' }}>{i18n.t.trending_author_tips}</button>
                <button onClick={() => setAuthorRankType('income')} class="px-3 py-1 rounded-md" classList={{ 'bg-[var(--bg-700)] text-white': authorRankType() === 'income' }}>{i18n.t.trending_author_income}</button>
              </div>
            </div>

            {/* Author Ranking List */}
            <div class="flex flex-col gap-3">
              <Show when={!authorRankings.loading} fallback={<div class="text-center py-10 opacity-50">Loading...</div>}>
                <For each={authorRankings()}>
                  {(item) => (
                    <div 
                      onClick={() => handleAuthorClick(item.userId)}
                      class={`flex items-center gap-3 p-3 rounded-2xl border border-[var(--bg-700)] ${getRankColor(item.id)} bg-opacity-20 transition-all hover:scale-[1.01] cursor-pointer`}
                    >
                      <div class="text-xl font-black italic w-6 text-center">{item.id}</div>
                      <div class="h-12 w-12 rounded-full bg-[var(--bg-700)] overflow-hidden border-2 border-white/20">
                        <Show when={item.avatar} fallback={<div class="w-full h-full bg-gradient-to-br from-gray-400 to-gray-600" />}>
                          <img src={getAssetUrl(item.avatar)} class="w-full h-full object-cover" />
                        </Show>
                      </div>
                      <div class="flex-1 min-w-0">
                        <div class="text-sm font-bold truncate">{item.name}</div>
                        <div class="text-[10px] text-white/60 line-clamp-1">{item.desc}</div>
                      </div>
                      <div class="flex flex-col items-end gap-1">
                        <div class="flex items-center gap-1 text-[var(--hl-500)] font-bold">
                          <Gem size={12} class="text-blue-400" />
                          <span class="text-xs">{item.score}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </For>
              </Show>
            </div>
          </div>
        </Match>
      </Switch>
    </div>
  )
}

export default TrendingPage
